import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { createSharedProcessingMutationOptions } from "../api/sharedProcessings";
import * as FormDialog from "../shared/FormDialog";
import { trackEvent } from "../shared/Snowplow";

type CreateSharedProcessingDialogProps = { onClose: () => void; onCreated: (id: string) => void };

type FormValues = { name: string };

export const CreateSharedProcessingDialog: FC<CreateSharedProcessingDialogProps> = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const createMutation = useMutation(createSharedProcessingMutationOptions());
  const { handleSubmit, control, setFocus } = useForm<FormValues>({ defaultValues: { name: "" } });

  FormDialog.useAutoFocusField(setFocus, "name");

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreateRegistration.Dialog.CreateTitle")}</FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(({ name }) => {
          trackEvent.createRegistrationClick();
          return createMutation.mutate(
            { name },
            {
              onSuccess: ({ id }) => {
                onClose();
                onCreated(id);
              },
            }
          );
        })}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={createMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreateRegistration.Dialog.RegistrationError") }}
              label={t("CreateRegistration.Dialog.NameLabel")}
              placeholder={t("CreateRegistration.Dialog.Shared.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={createMutation.isPending}>
            {t("CreateRegistration.Dialog.CreateSubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
